import { posix } from "path";
import { URL } from "url";
import { Message } from "vscode-jsonrpc";

export type Validator = (uri: string) => void;

export function workspaceValidator(workspace: string): Validator {
  return (uri: string) => {
    const url = new URL(uri);
    const normalizedPath = posix.normalize(url.pathname);
    const normalizedWorkspace = posix.normalize(workspace);

    if (!normalizedPath.startsWith(normalizedWorkspace)) {
      throw new Error(`Path ${uri} is not in workspace ${workspace}`);
    }
  }
}

export function disallowedFileExtensionValidator(disallowedExtensions: string[]): Validator {
  return (uri: string) => {
    const url = new URL(uri);
    const normalizedPath = posix.normalize(url.pathname);

    disallowedExtensions.forEach((extension) => {
      if (normalizedPath.endsWith(extension)) {
        throw new Error(`Path ${uri} has disallowed file extension ${extension}`);
      }
    });
  }
}

export function multiValidator(...validators: Validator[]): Validator {
  return (uri: string) => {
    validators.forEach((validator) => validator(uri));
  }
}

export function validateMessageUris(message: Message, ...validators: Validator[]): void {
  traverseAndValidateUris(message, multiValidator(...validators));
}

/**
 * Helper function to check if a field name indicates it contains a URI
 */
function isUriField(fieldName: string): boolean {
  return fieldName === "uri" || fieldName.endsWith("Uri") || fieldName.endsWith("Uris");
}

/**
 * Validates a single URI value or array of URIs
 */
function validateUriValue(value: unknown, validator: Validator): void {
  if (typeof value === "string") {
    validator(value);
  } else if (Array.isArray(value)) {
    value.forEach((item) => {
      if (typeof item === "string") {
        validator(item);
      }
    });
  }
}

/**
 * Recursively traverses a message structure and validates all URI fields
 */
function traverseAndValidateUris(message: unknown, validator: Validator): void {
  if (message === null || message === undefined) {
    return;
  }

  if (Array.isArray(message)) {
    message.forEach((item) => traverseAndValidateUris(item, validator));
    return;
  }

  if (typeof message === "object" && !Array.isArray(message)) {
    for (const [key, value] of Object.entries(message)) {
      if (isUriField(key)) {
        validateUriValue(value, validator);
      } else {
        traverseAndValidateUris(value, validator);
      }
    }
  }
}
